package com.evoapps.evoshift.domain.calendar

/**
 * The calendar apps "Your shifts in your calendar" opens directly (SH-202), each with its own
 * add-a-subscription page, so nobody has to copy the private link by hand.
 */
enum class CalendarApp {
    /** Apple Calendar on iPhone, iPad and Mac: the webcal:// address opens its "Subscribe" dialog. */
    APPLE,

    /** Google Calendar: its web page with the address (the Android app cannot add one itself). */
    GOOGLE,

    /** Outlook.com and Hotmail accounts. */
    OUTLOOK,

    /** Outlook with a work or school account (Microsoft 365). */
    OUTLOOK_WORK
}

/** Where the app runs, which decides the order of the calendar buttons. */
enum class CalendarDevice { IOS, ANDROID, MAC, WEB, DESKTOP }

/**
 * The links that add the private calendar feed (functions/calendarFeed.js, an https address with a
 * secret token) to a calendar app in one tap (SH-202). Formats checked 2026-10-21:
 * - Apple: `webcal://host/path?query` (the feed address with the webcal scheme).
 * - Google: `https://calendar.google.com/calendar/render?cid=<webcal address, percent-encoded>`.
 * - Outlook: `https://outlook.live.com/calendar/0/addfromweb?url=<https address>&name=<name>`,
 *   and outlook.office.com for work accounts.
 */
object CalendarSubscriptionLinks {
    private const val GOOGLE = "https://calendar.google.com/calendar/render?cid="
    private const val OUTLOOK = "https://outlook.live.com/calendar/0/addfromweb?url="
    private const val OUTLOOK_WORK = "https://outlook.office.com/calendar/0/addfromweb?url="

    /** The calendar's name in Outlook: "EVoShift – [personName]", or "EVoShift" without a name. */
    fun calendarName(personName: String?): String =
        personName?.trim()?.takeIf { it.isNotEmpty() }?.let { "EVoShift – $it" } ?: "EVoShift"

    /** [feedUrl] (https or http) with the webcal scheme; other addresses stay as they are. */
    fun webcal(feedUrl: String): String {
        val url = feedUrl.trim()
        return when {
            url.startsWith("https://", ignoreCase = true) -> "webcal://" + url.substring("https://".length)
            url.startsWith("http://", ignoreCase = true) -> "webcal://" + url.substring("http://".length)
            else -> url
        }
    }

    /** The https address for [feedUrl] given as webcal:// (Outlook and "Copy link" want https). */
    fun https(feedUrl: String): String {
        val url = feedUrl.trim()
        return if (url.startsWith("webcal://", ignoreCase = true)) "https://" + url.substring("webcal://".length) else url
    }

    /** The address that adds [feedUrl] to [app], the calendar called [name] where the app names it. */
    fun link(app: CalendarApp, feedUrl: String, name: String = calendarName(null)): String = when (app) {
        CalendarApp.APPLE -> webcal(feedUrl)
        CalendarApp.GOOGLE -> GOOGLE + encode(webcal(feedUrl))
        CalendarApp.OUTLOOK -> OUTLOOK + encode(https(feedUrl)) + "&name=" + encode(name)
        CalendarApp.OUTLOOK_WORK -> OUTLOOK_WORK + encode(https(feedUrl)) + "&name=" + encode(name)
    }

    /** The buttons, the likely calendar first: Apple on Apple devices, Google on Android and elsewhere. */
    fun order(device: CalendarDevice): List<CalendarApp> = when (device) {
        CalendarDevice.IOS, CalendarDevice.MAC ->
            listOf(CalendarApp.APPLE, CalendarApp.GOOGLE, CalendarApp.OUTLOOK, CalendarApp.OUTLOOK_WORK)
        CalendarDevice.ANDROID, CalendarDevice.WEB, CalendarDevice.DESKTOP ->
            listOf(CalendarApp.GOOGLE, CalendarApp.APPLE, CalendarApp.OUTLOOK, CalendarApp.OUTLOOK_WORK)
    }

    /** Percent-encodes [text] as a query value: UTF-8, only the unreserved characters (RFC 3986) stay. */
    fun encode(text: String): String = buildString {
        text.encodeToByteArray().forEach { byte ->
            val b = byte.toInt() and 0xFF
            val c = b.toChar()
            if (c in 'A'..'Z' || c in 'a'..'z' || c in '0'..'9' || c == '-' || c == '_' || c == '.' || c == '~') {
                append(c)
            } else {
                append('%').append(HEX[b shr 4]).append(HEX[b and 0x0F])
            }
        }
    }

    private const val HEX = "0123456789ABCDEF"
}
